// src/pages/usa/colorado/Aspen.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Aspen ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";

export default function Aspen() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__aspenBackRouteSet) {
      window.__aspenBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>

      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Aspen, Colorado</h1>

        <section className="travel-review">
          <div className="travel-text">
            <h2>Discover Aspen, Colorado</h2>


            <p>
              Aspen is widely known as a haven for skiing and snowboarding — a winter playground for athletes chasing
              fresh powder. But in my experience, Aspen is much more than that. Both in summer and winter, the town
              transforms into a lifestyle destination, where the energy shifts from adrenaline to appreciation.
            </p>

            <p>
              Instead of crowds rushing to the slopes, you’ll find people enjoying a wide range of activities: hiking
              through wildflower-covered trails, riding the Silver Queen Gondola for mountaintop yoga or live music,
              exploring the Maroon Bells, dining outdoors under the stars, or catching a performance at the Wheeler
              Opera House. Aspen becomes a place to slow down, connect, and experience the mountains in a whole new way.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              Heading to Aspen? You’ve got options — and each one offers its own kind of adventure. Most travelers fly
              into Denver International Airport (DEN) for better connections and pricing, then make the scenic drive
              west. It’s about 3.5 to 4 hours by car, depending on the route and season.
            </p>

            <p>
              In summer, the most stunning drive is via Independence Pass (Highway 82), which climbs over the
              Continental Divide with hairpin turns and jaw-dropping views. Just note: it’s closed in winter due to
              snow. For year-round access, take I-70 west to Glenwood Springs, then Highway 82 south into Aspen — a
              gentler route that still delivers mountain charm.
            </p>

            <p>
              Prefer to fly straight in? Aspen–Pitkin County Airport (ASE) is just minutes from downtown, with direct
              flights from major hubs during peak seasons. It’s small, but mighty — and landing between the peaks is an
              experience in itself.
            </p>

            {/* 🌟 MY PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Independence Pass:</strong> In my opinion, this is one of the most beautiful drives during the
                  fall season. It can be driven from Aspen to Twin Lakes or the opposite direction. The changing colors
                  of the trees, the charming towns along the way, and the variety of hiking spots make this route
                  unforgettable. Note: the pass is closed from October to May for safety.<br />
                  <a
                    href="https://www.google.com/maps?q=39.14801649968753,-106.77720969822201"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Independence Pass
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Grottos Ice Cave:</strong> Located along the Independence Pass drive, this short hike offers
                  stunning scenery and leads to a unique ice cave formation.<br />
                  <a
                    href="https://www.google.com/maps?q=39.11805511604977,-106.70054813225936"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Grottos Ice Cave
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Independence Pass Outlook Point:</strong> Also found along the same drive, this viewpoint is
                  just a short walk from the parking area and offers panoramic views of the surrounding peaks.<br />
                  <a
                    href="https://www.google.com/maps?q=39.10828866390008,-106.56401429414385"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Independence Pass Outlook
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Main Town of Aspen:</strong> Whether you're stopping in for a bite, sipping coffee, browsing
                  local shops, or simply relaxing, Aspen offers a cozy and vibrant experience. It’s a small town that
                  can be explored in a day, but its atmosphere lingers much longer. One of my favorite moments was
                  riding the gondola — the views are stunning, and each town’s gondola seems to carry its own unique
                  enchantment.<br />
                  <a
                    href="https://www.google.com/maps?q=39.19107072927115,-106.82123290092102"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Aspen Town
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Snowmass:</strong> This small town, part of the greater Aspen area, is absolutely worth a
                  visit. The atmosphere feels distinct from Aspen’s main town — quieter, more spacious, and layered into
                  the hillside. Instead of separate buildings scattered across town, Snowmass gives the impression of a
                  single, sprawling villa, with structures staggered in tiers that follow the mountain’s slope.<br />
                  <a
                    href="https://www.google.com/maps?q=39.20622021487982,-106.95374215693259"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Snowmass Town
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Ashcroft Ghost Town:</strong> I know — this might not be everyone’s kind of stop, but in the
                  fall, it was unforgettable. The colors were at their peak: blazing golds, deep rusts, and fiery reds
                  that seemed to set the whole valley aglow. It felt like the landscape was putting on a final,
                  brilliant show before winter. My eyes couldn’t stop chasing the color.<br />
                  <a
                    href="https://www.google.com/maps?q=39.05585359084933,-106.79956618809064"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Ashcroft Gost Town
                  </a>
                </div>
              </li>
            </ul>

            {/* 🏔️ HIDDEN GEMS */}
            <h3>🏔️ Hidden Gems in Aspen</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Maroon Bells Scenic Area:</strong> Iconic twin peaks above a mirror-like lake. Crater Lake
                  Trail is a must.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Lost Man Trail:</strong> Meadows, alpine lakes, and golden aspens. Quiet and cinematic.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Conundrum Hot Springs:</strong> A remote 9-mile trek to natural hot springs. Permits required.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Magic Mushroom House:</strong> A psychedelic architectural oddity visible from the road.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Smuggler Mine Tour:</strong> Explore Aspen’s silver mining history underground.
                </div>
              </li>
            </ul>

            {/* 🛏️ PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Aspen</h3>

            <p>
              Aspen offers year-round lodging from luxury ski-in/ski-out resorts to boutique hotels. These top-ranked
              stays consistently earn high marks:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>The Little Nell:</strong> Aspen’s only five-star ski-in/ski-out hotel.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Hotel Jerome:</strong> Historic Victorian charm with modern luxury.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Limelight Hotel Aspen:</strong> Stylish, central, and great for festivals.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>St. Regis Aspen Resort:</strong> Spa, pool, and mountain views.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Aspen Meadows Resort:</strong> Quiet, nature-surrounded, Bauhaus-inspired.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Viceroy Snowmass:</strong> Luxury resort ideal for families and skiers.
                </div>
              </li>
            </ul>

            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>

            {/* 🎥 VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos I took from Grottos Ice Cave and Ashcroft Ghost Town.</p>

        <p>
  <a
    href="https://youtu.be/MAogFRBcxYg?si=EzO3CU42aqovTkgk"
    target="_blank"
    rel="noopener noreferrer"
  >
    Grottos Ice Cave
  </a>
</p>

<p>
  <a
    href="https://youtu.be/UU5ZKk1swjg?si=myegTrwTdy7-JeHo"
    target="_blank"
    rel="noopener noreferrer"
  >
    Ashcroft Ghost Town
  </a>
</p>
<p>
  <a
    href="https://youtu.be/t4S72qF2Mzg?si=aNa9xLjic9vodvW_"
    target="_blank"
    rel="noopener noreferrer"
  >
    Independence Pass Video
  </a>
</p>

</div>

{/* RIGHT COLUMN — IMAGES */}
<div className="travel-image-column">
  <a
    href="https://youtu.be/UU5ZKk1swjg?si=myegTrwTdy7-JeHo"
    target="_blank"
    rel="noopener noreferrer"
    className="youtube-image-card"
  >
    <img
      src="/images/usa/colorado/aspen/ashcrofthotel.jpg"
      alt="Ashcroft Ghost Town Video"
    />
  </a>

  <a
    href="https://youtu.be/t4S72qF2Mzg?si=Co1HZndXVG1u4IxR"
    target="_blank"
    rel="noopener noreferrer"
    className="youtube-image-card"
  >
    <img
      src="/images/usa/colorado/aspen/ip.jpg"
      alt="Independence Pass Video"
    />
  </a>

  <a
    href="https://youtu.be/MAogFRBcxYg?si=EzO3CU42aqovTkgk"
    target="_blank"
    rel="noopener noreferrer"
    className="youtube-image-card"
  >
    <img
      src="/images/usa/colorado/aspen/insidegrotos2.jpg"
      alt="Grottos Ice Cave Video"
    />
  </a>

  <img src="/images/usa/colorado/aspen/aspeninfall.jpg" alt="Aspen in Fall" />
  <img src="/images/usa/colorado/aspen/aspenclock.jpg" alt="Aspen Clock" />
  <img src="/images/usa/colorado/aspen/aspenstreet.jpg" alt="Aspen Street" />
  <img src="/images/usa/colorado/aspen/aspenfromgondola.jpg" alt="Aspen Gondola View" />
  <img src="/images/usa/colorado/aspen/aspentown.jpg" alt="Aspen Town" />
  <img src="/images/usa/colorado/aspen/yogastation.jpg" alt="Yoga Station" />
  <img src="/images/usa/colorado/aspen/fallinashcroft.jpg" alt="Fall in Ashcroft" />
  <img src="/images/usa/colorado/aspen/gotofa.jpg" alt="Ghost Town Fall" />
  <img src="/images/usa/colorado/aspen/snowmass.jpg" alt="Snowmass" />
  <img src="/images/usa/colorado/aspen/snowmassgondola.jpg" alt="Snowmass Gondola" />
  <img src="/images/usa/colorado/aspen/snowmassbike.jpg" alt="Snowmass Bike" />
  <img src="/images/usa/colorado/aspen/riatgro.jpg" alt="River at Grottos" />
</div>
</section>
</div>

{/* Centered email button */}
<div className="email-wrapper">
  <button className="email-link" onClick={() => setOpen(true)}>
    contact@thefindingspot.com
  </button>
</div>

{/* Popup */}
<ContactPopup isOpen={open} onClose={() => setOpen(false)} />

<Footer />
</>
);
}
